import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'

import { executeGet } from '@/services/lukita'

export interface ConfigFieldOption {
  key: string
  label: string
}

export interface ConfigField {
  key: string
  label: string
  description?: string
  type: 'text' | 'date' | 'select'
  required?: boolean
  options?: ConfigFieldOption[]
}

export interface ConfigCaseType {
  key: string
  label: string
  fields: ConfigField[]
}

export interface ConfigAccountType {
  key: string
  label: string
}

// Report section catalog, passed through untouched for analysis to interpret.
export interface ConfigSection {
  key: string
  label: string
  description: string
  placeholder: string
  pinned: boolean
}

// The three case answers a project owns, named as the project serializes them.
export interface CaseAnswers {
  caseType: string
  caseDetails: Record<string, string>
  caseAccountsType: string
}

export interface CaseTypeConfig {
  caseTypes: ConfigCaseType[]
  accountTypes: ConfigAccountType[]
}

export interface CaseDetailEntry {
  key: string
  label: string
  value: string
}

export interface CaseHeadline {
  caseTypeLabel?: string
  accountTypeLabel?: string
}

export const DEFAULT_CASE_TYPE = 'general'
export const DEFAULT_CASE_ACCOUNTS_TYPE = 'personal'

// The wire serves case types only; account scopes are a fixed client list.
export const CASE_ACCOUNT_TYPES: ConfigAccountType[] = [
  { key: 'personal', label: 'Personal Accounts' },
  { key: 'business', label: 'Business Accounts' },
  { key: 'both', label: 'Both Personal & Business' },
]

export const caseTypeFields = (
  caseTypes: ConfigCaseType[],
  caseType: string,
): ConfigField[] => caseTypes.find(({ key }) => key === caseType)?.fields ?? []

// Labels come back undefined while the config is still loading, so callers can
// wait instead of showing a raw key.
export const resolveCaseHeadline = (
  answers: CaseAnswers,
  config: CaseTypeConfig,
): CaseHeadline => ({
  caseTypeLabel: config.caseTypes.find(({ key }) => key === answers.caseType)
    ?.label,
  accountTypeLabel: config.accountTypes.find(
    ({ key }) => key === answers.caseAccountsType,
  )?.label,
})

export const resolveCaseDetails = (
  answers: CaseAnswers,
  config: CaseTypeConfig,
): CaseDetailEntry[] =>
  caseTypeFields(config.caseTypes, answers.caseType)
    .map((field) => ({
      key: field.key,
      label: field.label,
      value: answers.caseDetails?.[field.key]?.trim() ?? '',
    }))
    .filter((entry) => entry.value.length > 0)

export const caseSummaryText = (headline: CaseHeadline): string =>
  [headline.caseTypeLabel, headline.accountTypeLabel]
    .filter(Boolean)
    .join(' · ') || 'Case details'

interface CaseTypesResponse {
  caseTypes: (ConfigCaseType & { suggestedSections: string[] })[]
  sections: ConfigSection[]
}

interface CaseConfigStoreLoaders {
  fetchCaseConfig: () => Promise<void>
}

interface CaseConfigStore {
  isLoaded: boolean
  isLoading: boolean
  isFetching: boolean
  caseTypes: ConfigCaseType[]
  accountTypes: ConfigAccountType[]
  suggestedSections: Record<string, string[]>
  sections: ConfigSection[]
  loaders: CaseConfigStoreLoaders
}

export const useCaseConfigStore = create<CaseConfigStore>((set, get) => ({
  isLoaded: false,
  isLoading: true,
  isFetching: false,
  caseTypes: [],
  accountTypes: [],
  suggestedSections: {},
  sections: [],

  loaders: {
    // A failure settles and rethrows so callers can fall back; the next call retries.
    fetchCaseConfig: async () => {
      const { isLoaded, isFetching } = get()
      if (isLoaded || isFetching) return

      set({ isFetching: true, isLoading: true })

      const response = await executeGet('/case_types').catch((error) => {
        set({ isFetching: false, isLoading: false })
        throw error
      })
      const result = await response.json()
      const parsed = camelcaseKeys(result, { deep: true }) as CaseTypesResponse

      set({
        isFetching: false,
        isLoading: false,
        isLoaded: true,
        caseTypes: parsed.caseTypes.map(
          ({ suggestedSections, ...caseType }) => caseType,
        ),
        accountTypes: CASE_ACCOUNT_TYPES,
        suggestedSections: Object.fromEntries(
          parsed.caseTypes.map(({ key, suggestedSections }) => [
            key,
            suggestedSections,
          ]),
        ),
        sections: parsed.sections,
      })
    },
  },
}))
